import React, { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Link, alpha, useTheme } from "@mui/material";
import { InsertDriveFile } from "@mui/icons-material";
import { downloadFile } from "@frontend/features/common/queries";
import { useTranslation } from "react-i18next";
import { usesLightDocumentForeground } from '../../documentPresentation';

interface ChatDocumentProps {
  fileId: string;
  caption?: string;
  filename: string;
  inbound?: boolean | null;
  size?: 'regular' | 'compact';
}

export const ChatDocument: React.FC<ChatDocumentProps> = ({
  fileId,
  caption,
  filename,
  inbound = false,
  size = 'regular'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documentSource, setDocumentSource] = useState<string>();
  const theme = useTheme();
  const { t } = useTranslation();

  const compact = size === 'compact';

  useEffect(() => {
    setIsLoading(true);
    if (fileId) {
      downloadFile(+fileId, true)
        .then((url: any) => {
          if (!url) return;
          setDocumentSource(url);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [fileId]);

  const boxMaxWidth = compact ? 260 : 360;
  const iconSize = compact ? 36 : 48;

  const hasLightForeground = usesLightDocumentForeground(inbound);

  // Neutral background for compact and unknown attribution, otherwise respect inbound.
  const bgColor = compact
    ? theme.palette.grey[100]
    : (hasLightForeground ? "grey.100" : alpha(theme.palette.common.white, 0.1));

  const linkColor = compact
    ? theme.palette.text.primary
    : (hasLightForeground ? theme.palette.primary.main : theme.palette.common.white);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="center"
      width="100%"
      gap={1}
    >
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 1, height: compact ? 64 : 80 }} />
      ) : documentSource ? (
        <Box
          maxWidth={boxMaxWidth}
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding={compact ? 1.5 : 2}
          border={1}
          borderColor="divider"
          borderRadius={1}
          bgcolor={bgColor}
        >
          <InsertDriveFile
            sx={{
              fontSize: iconSize,
              color: compact ? theme.palette.text.primary : (hasLightForeground ? theme.palette.primary.main : theme.palette.common.white)
            }}
          />
          <Box mt={1} width="100%" textAlign="center">
            <Link
              href={documentSource}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: linkColor,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {filename || t('common.view')}
              </Typography>
            </Link>
          </Box>
        </Box>
      ) : null}
      {caption && size !== 'compact' && (
        <Typography variant="body2" fontWeight="medium">
          {caption}
        </Typography>
      )}
    </Box>
  );
}; 
